import { Paper, Box, CircularProgress, Typography, Chip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { UsageLog } from '@/types/domain';
import { getActionLabel, getActionColor } from '../utils';

interface LogTableProps {
  logs: UsageLog[];
  loading: boolean;
  totalCount: number;
  paginationModel: { page: number; pageSize: number };
  onPaginationChange: (model: { page: number; pageSize: number }) => void;
}

export default function LogTable({
  logs,
  loading,
  totalCount,
  paginationModel,
  onPaginationChange,
}: LogTableProps) {
  const { t } = useTranslation();

  const columns: GridColDef<UsageLog>[] = [
    { field: 'id', headerName: t('logs.id'), width: 80 },
    {
      field: 'device_id',
      headerName: t('logs.deviceId'),
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          #{params.value}
        </Typography>
      ),
    },
    {
      field: 'hardware_id',
      headerName: t('logs.hardwareId'),
      width: 200,
      valueGetter: (_value, row) => row?.hardware_id || '-',
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontFamily="monospace"
          sx={{ fontSize: '0.75rem' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'device_name',
      headerName: t('logs.deviceName'),
      width: 180,
      valueGetter: (_value, row) => row?.device_name || '-',
    },
    {
      field: 'action',
      headerName: t('logs.action'),
      width: 150,
      renderCell: (params) => (
        <Chip
          label={getActionLabel(params.value, t)}
          color={getActionColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'ip_address',
      headerName: t('logs.ipAddress'),
      width: 150,
      valueGetter: (_value, row) => row?.ip_address || '-',
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'details',
      headerName: t('logs.details'),
      width: 200,
      valueGetter: (_value, row) => row?.details || '-',
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: t('logs.createdAt'),
      width: 180,
      renderCell: (params) => {
        try {
          const value = params?.row?.created_at;
          if (!value) return '-';
          const dateStr = value.replace(' ', 'T');
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '-';
          return format(date, 'yyyy-MM-dd HH:mm:ss');
        } catch {
          return '-';
        }
      },
    },
  ];

  return (
    <Paper>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={logs}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          sx={{
            '& .MuiDataGrid-row.even': {
              bgcolor: 'action.hover',
            },
          }}
        />
      )}
    </Paper>
  );
}
